import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ORDER_STATUS } from '../orderStatus';
import { api } from '../api';
import { BRAND_NAME } from '../brand';
import OrderModal from '../components/OrderModal';
import Tooltip from '../components/Tooltip';
import { paymentMethodLabel } from '../utils/paymentMethod';
import { useDeliverySlots, unionWithOrderSlots, compareBySlot, compareByReadyTime } from '../deliverySlots';
import { slotText } from '../components/SlotChip';
import { openOrdersStream } from '../utils/ordersStream';

// Значок «!» у заказа: заметка администратора, комментарий курьеру и окно доставки внутри слота —
// друг под другом (владелец 25.10: комментарий курьеру отдельно не выводился)
const orderNote = (o) => [
  o.admin_comment && `Заметка: ${o.admin_comment}`,
  o.courier_comment && `Курьеру: ${o.courier_comment}`,
  (o.deliver_from || o.deliver_to) && `Окно доставки: ${o.deliver_from || '…'}–${o.deliver_to || '…'}`,
].filter(Boolean).join('\n\n');

// «Сегодня» в ряду дней — подчёркнутая терракотой дата: видна и на белом, и на выбранном
// тёмном дне, отдельная рамка у одной плашки ломала вид группы (владелец 25.10)
const TODAY_MARK = { textDecoration: 'underline', textDecorationColor: '#ED956F', textDecorationThickness: 2, textUnderlineOffset: 4 };

const statusLabels = {
  new: { ...ORDER_STATUS.new, icon: '/icons/nv-status-new.svg' },
  packaging: { ...ORDER_STATUS.packaging, icon: '/icons/nv-package.svg' },
  ready: { ...ORDER_STATUS.ready, icon: '/icons/nv-status-ready.svg' },
  courier: { ...ORDER_STATUS.courier, icon: '/icons/nv-delivery.svg' },
  completed: { ...ORDER_STATUS.completed, icon: '/icons/nv-status-completed.svg' },
};

const paymentStatusLabels = {
  pending:      { label: 'Ожидает',           class: 'new' },
  paid:         { label: 'Оплачен',           class: 'completed' },
  paid_chat:    { label: 'Оплачен (чат)',     class: 'completed' },
  failed:       { label: 'Ошибка оплаты',     class: 'cancelled' },
  expired:      { label: 'Истёк срок оплаты', class: 'cancelled' },
  cancelled:    { label: 'Отменён',           class: 'cancelled' },
  not_required: { label: 'Не требуется',      class: 'info' }
};

function CalendarPage({ showToast }) {
  const { timeSlots: configSlots } = useDeliverySlots();
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [weekDates, setWeekDates] = useState([]);
  const [ordersGrouped, setOrdersGrouped] = useState({
    new: [],
    packaging: [],
    ready: [],
    courier: [],
    completed: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [draggedOrder, setDraggedOrder] = useState(null);
  const [packerWorkflowEnabled, setPackerWorkflowEnabled] = useState(false);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  const [weekCounts, setWeekCounts] = useState({}); // { 'YYYY-MM-DD': N } — заказы на каждый день
  const loadOrdersForDateRef = useRef(null);
  const selectedDateRef = useRef(selectedDate);
  const ignoreSSEForOrders = useRef(new Set());
  const showToastRef = useRef(showToast);
  useEffect(() => { showToastRef.current = showToast; }, [showToast]);

  useEffect(() => {
    generateWeek(new Date());
    loadPackerWorkflowSetting();
  }, []);

  const loadPackerWorkflowSetting = async () => {
    try {
      const res = await api.getPackerWorkflowEnabled();
      setPackerWorkflowEnabled(res.enabled);
    } catch (error) {
      console.error('Failed to load packer workflow setting', error);
    }
  };

  // Получить активные статусы (с учётом режима упаковки)
  const getActiveStatuses = () => {
    if (packerWorkflowEnabled) {
      return Object.keys(statusLabels);
    } else {
      return Object.keys(statusLabels).filter(s => s !== 'packaging');
    }
  };

  const loadOrdersForDate = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.getOrdersByDate(selectedDate);
      setOrdersGrouped(data);
    } catch (error) {
      showToastRef.current('Ошибка загрузки заказов', 'error');
      setOrdersGrouped({
        new: [],
        packaging: [],
        ready: [],
        courier: [],
        completed: []
      });
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (selectedDate) {
      loadOrdersForDate();
      setSelectedTimeSlots([]); // Сбрасываем фильтр при смене даты
    }
  }, [selectedDate, loadOrdersForDate]);

  // Подгружаем счётчики заказов на каждую видимую дату недели
  useEffect(() => {
    if (!weekDates.length) return;
    const from = weekDates[0];
    const to = weekDates[weekDates.length - 1];
    api.getOrderCountsByDate(from, to)
      .then(setWeekCounts)
      .catch(() => {});
  }, [weekDates]);

  // Сохраняем ссылки на функции и состояние для SSE
  const refreshSingleOrderRef = useRef(null);

  useEffect(() => {
    loadOrdersForDateRef.current = loadOrdersForDate;
  }, [loadOrdersForDate]);

  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  // SSE: автообновление при новом заказе и изменении статуса
  useEffect(() => {
    // Поток заказов только для сотрудников: пропуск и переподключение — в openOrdersStream
    const es = openOrdersStream();
    console.log('[Calendar] SSE connection opened');
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[Calendar] SSE event:', data);

        if (data.type === 'new_order' && data.delivery_date === selectedDateRef.current) {
          showToast(`Новый заказ #${data.order_number}`);
          setTimeout(() => {
            if (refreshSingleOrderRef.current && data.order_id) {
              refreshSingleOrderRef.current(data.order_id);
            }
          }, 100);
        } else if (data.type === 'order_updated') {
          if (data.delivery_date !== selectedDateRef.current) {
            return;
          }
          if (data.order_id && ignoreSSEForOrders.current.has(data.order_id)) {
            ignoreSSEForOrders.current.delete(data.order_id);
            return;
          }
          // Точечное обновление одного заказа вместо полного reload
          setTimeout(() => {
            if (refreshSingleOrderRef.current && data.order_id) {
              refreshSingleOrderRef.current(data.order_id);
            }
          }, 100);
        }
      } catch (e) {
        console.error('[Calendar] SSE error:', e);
      }
    };
    es.onerror = (error) => {
      console.error('[Calendar] SSE connection error:', error);
    };
    return () => {
      console.log('[Calendar] SSE connection closed');
      es.close();
    };
  }, []); // Empty dependency array - connection established once

  function getTodayDate() {
    const today = new Date();
    return formatDateValue(today);
  }

  function formatDateValue(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function formatDateDisplay(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const day = date.getDate();
    const month = date.toLocaleDateString('ru-RU', { month: 'short' });
    return `${day} ${month}`;
  }

  function getDayName(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
    return days[date.getDay()];
  }

  // Скользящее окно из 7 дней с centerDate в центре (3 до + центр + 3 после)
  function generateWeek(centerDate) {
    const dates = [];
    const center = new Date(centerDate);
    center.setHours(0, 0, 0, 0);
    for (let i = -3; i <= 3; i++) {
      const d = new Date(center);
      d.setDate(d.getDate() + i);
      dates.push(formatDateValue(d));
    }
    setWeekDates(dates);
  }

  function previousWeek() {
    // Сдвигаем окно на 7 дней назад (центр уезжает на -7)
    const center = new Date(weekDates[3] + 'T00:00:00');
    center.setDate(center.getDate() - 7);
    generateWeek(center);
  }

  function nextWeek() {
    const center = new Date(weekDates[3] + 'T00:00:00');
    center.setDate(center.getDate() + 7);
    generateWeek(center);
  }

  function currentWeek() {
    generateWeek(new Date());
    setSelectedDate(getTodayDate());
  }

  // Обновление одного заказа: загружаем актуальные данные с сервера
  const refreshSingleOrder = useCallback(async (orderId) => {
    try {
      console.log('[Calendar] Refreshing single order:', orderId);
      // Загружаем свежие данные заказа с сервера
      const updatedOrder = await api.getOrderById(orderId);

      setOrdersGrouped(prev => {
        const newGrouped = { ...prev };

        // Удаляем заказ из всех групп
        for (const status of Object.keys(newGrouped)) {
          newGrouped[status] = newGrouped[status].filter(o => o.id !== orderId);
        }

        // Добавляем обновленный заказ в соответствующую группу
        if (updatedOrder.status && newGrouped[updatedOrder.status]) {
          newGrouped[updatedOrder.status] = [...newGrouped[updatedOrder.status], updatedOrder];
        }

        return newGrouped;
      });

      // Если модальное окно открыто с этим заказом, обновляем selectedOrder
      setSelectedOrder(prev => {
        if (prev && prev.id === orderId) {
          return updatedOrder;
        }
        return prev;
      });
    } catch (error) {
      console.error('Failed to refresh single order:', error);
    }
  }, []);

  useEffect(() => {
    refreshSingleOrderRef.current = refreshSingleOrder;
  }, [refreshSingleOrder]);

  const openOrderModal = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  // Колбэк для оптимистичного обновления канбана при смене статуса из OrderModal
  const handleBeforeStatusChange = (orderId, newStatus, prevStatus) => {
    ignoreSSEForOrders.current.add(orderId);
    setOrdersGrouped(prev => {
      const newGrouped = { ...prev };
      let movedOrder = null;
      for (const s of Object.keys(newGrouped)) {
        const found = newGrouped[s].find(o => o.id === orderId);
        if (found) movedOrder = found;
        newGrouped[s] = newGrouped[s].filter(o => o.id !== orderId);
      }
      if (movedOrder && newGrouped[newStatus]) {
        newGrouped[newStatus] = [...newGrouped[newStatus], { ...movedOrder, status: newStatus }];
      }
      return newGrouped;
    });

    // Уведомление клиенту здесь НЕ шлём: бэкенд сам уведомляет при смене статуса
    // (PUT /orders/{id} → ready/courier/completed, для completed — с блоком отзыва).
    // Ручной вызов notifyOrderStatus дублировал сообщение: клиент получал голое
    // «Заказ завершён» + второе с просьбой об отзыве.
  };

  // Колбэк для оптимистичного удаления из канбана при отмене
  const handleBeforeCancellation = (orderId) => {
    ignoreSSEForOrders.current.add(orderId);
    setOrdersGrouped(prev => {
      const newGrouped = { ...prev };
      for (const s of Object.keys(newGrouped)) {
        newGrouped[s] = newGrouped[s].filter(o => o.id !== orderId);
      }
      return newGrouped;
    });
  };

  // Колбэк после любого обновления заказа — рефрешим канбан
  const handleOrderUpdated = () => {
    refreshSingleOrder(selectedOrder?.id).catch(() => {});
  };

  // Drag & Drop handlers
  const handleDragStart = (order) => {
    setDraggedOrder(order);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();

    if (!draggedOrder || draggedOrder.status === newStatus) {
      setDraggedOrder(null);
      return;
    }

    try {
      // Игнорируем SSE событие для этого заказа
      ignoreSSEForOrders.current.add(draggedOrder.id);

      // Оптимистичное перемещение — мгновенно двигаем карточку в новую колонку
      setOrdersGrouped(prev => {
        const newGrouped = { ...prev };
        for (const status of Object.keys(newGrouped)) {
          newGrouped[status] = newGrouped[status].filter(o => o.id !== draggedOrder.id);
        }
        const movedOrder = { ...draggedOrder, status: newStatus };
        if (newGrouped[newStatus]) {
          newGrouped[newStatus] = [...newGrouped[newStatus], movedOrder];
        }
        return newGrouped;
      });

      await api.updateOrder(draggedOrder.id, { status: newStatus });

      showToast('Статус заказа обновлён');
    } catch (error) {
      showToast('Ошибка обновления статуса', 'error');
    } finally {
      setDraggedOrder(null);
    }
  };

  const totalOrders = Object.values(ordersGrouped).reduce((sum, orders) => sum + orders.length, 0);
  // Сетка из настроек + слоты загруженных заказов: старые заказы
  // с архивным слотом не должны выпадать из фильтра
  const filterSlots = unionWithOrderSlots(configSlots, Object.values(ordersGrouped).flat());

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Календарь заказов</h1>
      </div>

      {/* Навигация по неделям */}
      <div style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        marginBottom: 12,
        padding: '12px 0'
      }}>
        {/* Стрелки — общий .btn-icon, «Сегодня» — второстепенная (UX-аудит 25.10) */}
        <button
          type="button"
          className="btn-icon"
          onClick={previousWeek}
          title="Прошлая неделя" aria-label="Прошлая неделя"
          style={{ width: 40, height: 40, fontSize: 18, fontWeight: 600 }}
        >
          ‹
        </button>

        {/* Дни — один переключатель .seg во всю ширину (владелец 25.10), колонки равные и не
            уже 64 px и своего текста. На телефоне лента прокручивается вбок, а не раздувает
            страницу (Safari отдалял её целиком). padding 1 — место для контура среза
            (::after выходит на 1 px), иначе появлялась лишняя прокрутка.
            nv-hscroll-hidden — без полосы прокрутки: на Mac её ползунок белой линией ложился
            на тёмный выбранный день; листать пальцем/тачпадом можно и без неё */}
        <div className="nv-hscroll-hidden" style={{ flex: 1, minWidth: 0, overflowX: 'auto', padding: 1 }}>
          <div className="seg" role="group" aria-label="День недели"
            style={{ display: 'grid', gridAutoFlow: 'column', gridAutoColumns: 'minmax(64px, 1fr)', minWidth: 'max-content' }}>
            {weekDates.map(date => {
              const isToday = date === getTodayDate();
              const isSelected = date === selectedDate;

              return (
                <button
                  key={date}
                  type="button"
                  aria-pressed={isSelected}
                  aria-current={isToday ? 'date' : undefined}
                  title={isToday ? 'Сегодня' : undefined}
                  className={`seg-btn${isSelected ? ' on' : ''}`}
                  onClick={() => setSelectedDate(date)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                    fontWeight: isSelected || isToday ? 600 : 500
                  }}
                >
                  <span style={{ fontSize: 11, opacity: 0.85, textTransform: 'lowercase' }}>
                    {getDayName(date)}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={isToday ? TODAY_MARK : undefined}>{formatDateDisplay(date)}</span>
                    {weekCounts[date] > 0 && (
                      <span style={{ fontSize: 12, fontWeight: 500, opacity: 0.75 }}>
                        ({weekCounts[date]})
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          className="btn-icon"
          onClick={nextWeek}
          title="Следующая неделя" aria-label="Следующая неделя"
          style={{ width: 40, height: 40, fontSize: 18, fontWeight: 600 }}
        >
          ›
        </button>

        <button
          type="button"
          className="btn btn-secondary btn-md"
          onClick={currentWeek}
          style={{ marginLeft: 4, height: 40 }}
        >
          Сегодня
        </button>
      </div>

      {/* Временные слоты — один переключатель .seg во всю ширину под днями, колонки равные
          (владелец 25.10); выбрать можно несколько — у каждого выбранного .on. На узком
          экране лента прокручивается вбок, как дни */}
      <div className="nv-hscroll-hidden" style={{ overflowX: 'auto', padding: 1, marginBottom: 20 }}>
        {filterSlots.length > 0 && (
        <div className="seg" role="group" aria-label="Слоты доставки"
          style={{ display: 'grid', gridTemplateColumns: `repeat(${filterSlots.length}, 1fr)`, minWidth: 'max-content' }}>
          {filterSlots.map(slot => {
            const isSelected = selectedTimeSlots.includes(slot);
            return (
              <button
                key={slot}
                type="button"
                aria-pressed={isSelected}
                className={`seg-btn${isSelected ? ' on' : ''}`}
                onClick={() => {
                  if (isSelected) {
                    setSelectedTimeSlots(selectedTimeSlots.filter(s => s !== slot));
                  } else {
                    setSelectedTimeSlots([...selectedTimeSlots, slot]);
                  }
                }}
                style={{ whiteSpace: 'nowrap' }}
              >
                {slot}
              </button>
            );
          })}
        </div>
        )}
      </div>

      {/* Колонки со статусами */}
      {isLoading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 16,
          // align-items: start — каждая колонка займёт свою натуральную высоту по содержимому,
          // а не растянется до высоты самой длинной (это даёт «адаптацию под кол-во заказов»).
          alignItems: 'start',
          marginBottom: 24,
        }}>
          {getActiveStatuses().map((statusKey) => {
            const { label, icon } = statusLabels[statusKey];
            const allOrders = (ordersGrouped[statusKey] || []).slice();

            // Применяем фильтр по временным слотам («срочно» Flowwow лежит в своём слоте по готовности)
            const filteredOrders = selectedTimeSlots.length > 0
              ? allOrders.filter(order => selectedTimeSlots.includes(order.delivery_time || ''))
              : allOrders;

            const orders = filteredOrders.sort((a, b) => compareBySlot(a, b) || compareByReadyTime(a, b) || a.id - b.id);

            return (
              <div
                key={statusKey}
                style={{
                  background: '#fff',
                  border: '1px solid #D8DDDA',
                  '--ch': '8px', '--stroke': '#D8DDDA',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  // Колонка тянется по своему содержимому, но не выше остатка экрана.
                  // 240px = панель навигации + фильтры + page-header + паддинги main-content.
                  maxHeight: 'calc(100vh - 240px)',
                }}
              >
                {/* Заголовок колонки */}
                <div style={{
                  padding: '12px 14px',
                  background: '#F4F4EF',
                  borderBottom: '1px solid #D8DDDA',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#1C3D2E',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <img src={icon} alt="" style={{ width: 16, height: 16 }} />
                    <span>{label}</span>
                  </div>
                  <span style={{
                    background: '#fff',
                    border: '1px solid #D8DDDA',
                    padding: '2px 10px',
                    '--ch': '10px', '--stroke': '#D8DDDA',
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#1C3D2E'
                  }}>
                    {orders.length}
                  </span>
                </div>

                {/* Список заказов — занимает всю доступную высоту колонки */}
                <div
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, statusKey)}
                  style={{
                    padding: 12,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto'
                  }}
                >
                  {orders.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '24px 12px',
                      color: '#5E6561',
                      fontSize: 13
                    }}>
                      Нет заказов
                    </div>
                  ) : (
                    orders.map(order => {
                      const isPaid = order.payment_status === 'paid' || order.payment_status === 'paid_chat';
                      let pmDisplay = paymentMethodLabel(order.payment_method);
                      // «Счёт {бренд}» только при 100% бонусной оплате (total=0 + bonus_used>0).
                      if (isPaid && !pmDisplay && (order.total || 0) === 0 && (order.bonus_used || 0) > 0) pmDisplay = `Счёт ${BRAND_NAME}`;
                      const paymentLabel = isPaid
                        ? (pmDisplay ? `Оплачен · ${pmDisplay}` : 'Оплачен')
                        : (paymentStatusLabels[order.payment_status]?.label || 'Ожидает');
                      const paymentColor = isPaid ? '#1C3D2E' : order.payment_status === 'cancelled' ? '#5E6561' : '#B0603C';
                      return (
                      <div
                        key={order.id}
                        draggable
                        onDragStart={() => handleDragStart(order)}
                        onClick={() => openOrderModal(order)}
                        style={{
                          background: '#fff',
                          border: '1px solid #D8DDDA',
                          '--ch': '6px', '--stroke': '#D8DDDA',
                          padding: 12,
                          cursor: 'pointer',
                          transition: 'all 0.15s, --stroke 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(28,61,46,0.08)'; e.currentTarget.style.borderColor = '#1C3D2E'; e.currentTarget.style.setProperty('--stroke', '#1C3D2E'); }}
                        onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#D8DDDA'; e.currentTarget.style.setProperty('--stroke', '#D8DDDA'); }}
                      >
                        {/* Номер и время */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 8,
                          gap: 8
                        }}>
                          <span style={{
                            fontWeight: 700,
                            fontSize: 15,
                            color: '#1C3D2E',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            flexWrap: 'wrap'
                          }}>
                            #{order.order_type === 'subscription' ? `${order.subscription_order_number || order.subscription_id}/${order.subscription_delivery_number}` : (order.order_number || order.id)}{order.source_platform === 'flowwow' && order.external_platform_order_id && <span style={{ fontWeight: 500, color: '#C2185B' }}> / {order.external_platform_order_id}</span>}
                            {order.order_type === 'subscription' && (
                              <Tooltip text="Подписка"><img src="/icons/nv-stats-subscriptions.svg" alt="" style={{ width: 17, height: 17 }} /></Tooltip>
                            )}
                            {order.order_type === 'subscription' && order.items_count === 0 && (
                              <Tooltip text="Ожидает наполнения"><span style={{ fontSize: 11, background: '#FFE7DD', color: '#B0603C', padding: '2px 7px', '--ch': '4px', fontWeight: 600 }}>пусто</span></Tooltip>
                            )}
                            {orderNote(order) && (
                              <Tooltip text={orderNote(order)}><span style={{ display: 'inline-flex', width: 18, height: 18, borderRadius: '50%', background: '#B0603C', color: '#fff', fontSize: 12, fontWeight: 700, alignItems: 'center', justifyContent: 'center' }}>!</span></Tooltip>
                            )}
                            {order.odd_quantity && (
                              <Tooltip text="Нечётное количество"><img src="/icons/nv-odd.svg" alt="" style={{ width: 17, height: 17 }} /></Tooltip>
                            )}
                            {order.has_postcard && (
                              <Tooltip text="Открытка"><img src="/icons/mail.svg" alt="" style={{ width: 17, height: 17 }} /></Tooltip>
                            )}
                            {order.history?.length > 0 && (
                              <Tooltip text="Заказ редактировался"><img src="/icons/edit.svg" alt="" style={{ width: 16, height: 16 }} /></Tooltip>
                            )}
                            {order.adjustments?.some(a => a.status === 'pending' && a.type === 'surcharge') && (
                              <Tooltip text="Ожидает доплаты"><span style={{ fontSize: 11, padding: '2px 7px', '--ch': '4px', background: '#FFE7DD', color: '#B0603C', fontWeight: 700 }}>+₽</span></Tooltip>
                            )}
                            {order.adjustments?.some(a => a.status === 'pending' && a.type === 'refund') && (
                              <Tooltip text="Ожидает возврата"><span style={{ fontSize: 11, padding: '2px 7px', '--ch': '4px', background: '#DFF2E8', color: '#1C3D2E', fontWeight: 700 }}>−₽</span></Tooltip>
                            )}
                          </span>
                          <span style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#1C3D2E',
                            background: '#DFF2E8',
                            border: '1px solid #1C3D2E',
                            padding: '3px 10px',
                            '--ch': '5px', '--stroke': '#1C3D2E',
                            whiteSpace: 'nowrap',
                            letterSpacing: '0.02em'
                          }}>
                            {slotText(order)}
                          </span>
                        </div>

                        {/* Клиент */}
                        <div style={{
                          fontSize: 13,
                          marginBottom: 4,
                          fontWeight: 500,
                          color: '#1C3D2E'
                        }}>
                          {(() => {
                            if (order.admin_order_type === 'walkin') return order.customer_name || 'Трафик';
                            if (order.admin_order_type === 'internal') return order.recipient_name;
                            if (order.is_recipient_other) return order.recipient_name;
                            return `${order.user?.first_name || ''} ${order.user?.last_name || ''}`.trim() || '—';
                          })()}
                        </div>

                        {/* Адрес */}
                        <div style={{
                          fontSize: 12,
                          color: '#5E6561',
                          marginBottom: 8,
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 6
                        }}>
                          <img
                            src={order.delivery_type === 'pickup' ? '/icons/nv-pickup.svg' : '/icons/nv-delivery.svg'}
                            alt=""
                            style={{ width: 16, height: 16, marginTop: 1, flexShrink: 0 }}
                          />
                          <span>
                            {order.delivery_type === 'pickup'
                              ? 'Самовывоз'
                              : `${order.delivery_address || '—'}${order.city_district ? ` (${order.city_district})` : ''}`}
                          </span>
                        </div>

                        {/* Сумма и оплата */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 8,
                          paddingTop: 8,
                          borderTop: '1px solid #EBEDEC'
                        }}>
                          {(() => {
                            const items = order.items || [];
                            const composition = items.length > 0
                              ? items.map(i => `• ${i.product_name} ×${i.quantity}`).join('\n')
                              : null;
                            const priceEl = (
                              <span style={{
                                fontWeight: 700,
                                fontSize: 15,
                                color: '#B0603C'
                              }}>
                                {(order.total || 0).toLocaleString('ru-RU')} ₽
                              </span>
                            );
                            return composition ? <Tooltip text={composition}>{priceEl}</Tooltip> : priceEl;
                          })()}
                          <span style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: paymentColor,
                            whiteSpace: 'nowrap'
                          }}>
                            {paymentLabel}
                          </span>
                        </div>

                        {/* Флорист, Упаковщик и Курьер */}
                        {(order.florist || order.packer || order.courier) && (
                          <div style={{
                            fontSize: 14,
                            marginTop: 8,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 5,
                            color: '#5E6561'
                          }}>
                            {order.florist && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                                <img src="/icons/nv-florist.svg" alt="" style={{ width: 16, height: 16 }} />
                                Флорист: <span style={{ color: '#1C3D2E', fontWeight: 600 }}>{order.florist.name}</span>
                              </span>
                            )}
                            {order.packer && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                                <img src="/icons/nv-package.svg" alt="" style={{ width: 16, height: 16 }} />
                                Упак: <span style={{ color: '#1C3D2E', fontWeight: 600 }}>{order.packer.name}</span>
                              </span>
                            )}
                            {order.courier && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                                <img src="/icons/nv-delivery.svg" alt="" style={{ width: 16, height: 16 }} />
                                Курьер: <span style={{ color: '#1C3D2E', fontWeight: 600 }}>{order.courier.name}</span>
                              </span>
                            )}
                          </div>
                        )}

                        {/* Товары */}
                        {order.items_count > 0 && (
                          <div style={{
                            fontSize: 11,
                            color: '#5E6561',
                            marginTop: 6
                          }}>
                            {order.items_count} {order.items_count === 1 ? 'товар' : order.items_count > 4 ? 'товаров' : 'товара'}
                          </div>
                        )}
                      </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Модальное окно деталей заказа */}
      {showModal && selectedOrder && (
        <OrderModal
          initialOrder={selectedOrder}
          onClose={() => setShowModal(false)}
          showToast={showToast}
          onOrderUpdated={handleOrderUpdated}
          onBeforeStatusChange={handleBeforeStatusChange}
          onBeforeCancellation={handleBeforeCancellation}
        />
      )}
    </div>
  );
}

export default CalendarPage;
